/**
 * Temperature Role - Cast Pillar
 *
 * Normaliza input sujo para número tipado.
 * Detecta unidades em strings e converte se necessário.
 *
 * @example
 * import { castTemperature, castTemperatureStrict, detectTemperatureUnit } from '@attrx/role-morphic/temperature/cast';
 *
 * castTemperature('100 °C', 'celsius');       // 100
 * castTemperature('32 °F', 'celsius');        // 0 (converte)
 * castTemperatureStrict('100 °C');            // 100 (não converte)
 * detectTemperatureUnit('100 degrees F');     // 'fahrenheit'
 */

import { TEMPERATURE_UNITS, TEMPERATURE_ALIASES, type TemperatureUnit } from "./constants";
import { convertTemperature } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, detectando e convertendo unidade se presente
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo para conversão
 * @returns Número ou null se não puder converter
 *
 * @example
 * castTemperature(100, 'celsius');            // 100
 * castTemperature('100', 'celsius');          // 100
 * castTemperature('100 °C', 'celsius');       // 100
 * castTemperature('32 °F', 'celsius');        // 0 (converte F → C)
 * castTemperature('273.15 K', 'celsius');     // 0 (converte K → C)
 * castTemperature('invalid', 'celsius');      // null
 */
export function castTemperature(
  input: unknown,
  targetVariant: TemperatureUnit = "celsius"
): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    return parseTemperatureString(input, targetVariant);
  }

  return null;
}

/**
 * Faz cast sem conversão de unidade (só parseia o valor literal)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castTemperatureStrict('100 °C');   // 100 (ignora unidade, não converte)
 * castTemperatureStrict('100');      // 100
 * castTemperatureStrict(100);        // 100
 */
export function castTemperatureStrict(input: unknown): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    const numValue = parseNumber(input);
    return numValue;
  }

  return null;
}

/**
 * Versão safe de castTemperature que retorna Result
 */
export function tryCastTemperature(
  input: unknown,
  targetVariant: TemperatureUnit = "celsius"
): Result<number> {
  const result = castTemperature(input, targetVariant);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to temperature:${targetVariant}`,
    };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECTION
// =============================================================================

/**
 * Detecta unidade de temperatura a partir de uma string
 *
 * @param input - String com possível unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectTemperatureUnit('100 °C');           // 'celsius'
 * detectTemperatureUnit('100 degrees F');    // 'fahrenheit'
 * detectTemperatureUnit('273 K');            // 'kelvin'
 * detectTemperatureUnit('100');              // null
 */
export function detectTemperatureUnit(input: string): TemperatureUnit | null {
  const trimmed = input.trim().toLowerCase();
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);

  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  return detectUnit(unitStr);
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Parseia string para número, detectando unidade e convertendo se necessário
 */
function parseTemperatureString(input: string, targetVariant: TemperatureUnit): number | null {
  const trimmed = input.trim().toLowerCase();

  // Extrai número e possível unidade
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const [, numStr, unitStr] = match;

  // Normaliza número
  const numValue = parseNumber(numStr);
  if (numValue === null) {
    return null;
  }

  // Se tem unidade no input, converte se necessário
  const cleanUnitStr = unitStr.trim();
  if (cleanUnitStr) {
    const detectedUnit = detectUnit(cleanUnitStr);
    if (detectedUnit && detectedUnit !== targetVariant) {
      // Converte da unidade detectada para a target
      return convertTemperature(detectedUnit, targetVariant, numValue);
    }
  }

  return numValue;
}

/**
 * Parseia string numérica considerando formatos brasileiro e americano
 */
function parseNumber(numStr: string): number | null {
  // Remove espaços
  numStr = numStr.replace(/\s/g, "");

  const lastComma = numStr.lastIndexOf(",");
  const lastDot = numStr.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      // Brasileiro: 1.000,50
      numStr = numStr.replace(/\./g, "").replace(",", ".");
    } else {
      // Americano: 1,000.50
      numStr = numStr.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Só vírgulas: detecta se é milhar ou decimal
    const afterComma = numStr.split(",").slice(1);
    const isThousandSep = afterComma.every((part) => part.length === 3);
    if (isThousandSep) {
      numStr = numStr.replace(/,/g, "");
    } else {
      numStr = numStr.replace(",", ".");
    }
  } else if (!hasComma && hasDot) {
    // Só pontos: múltiplos pontos = milhar brasileiro
    const afterDot = numStr.split(".").slice(1);
    if (afterDot.length > 1) {
      numStr = numStr.replace(/\./g, "");
    }
  }

  const value = parseFloat(numStr);
  return isNaN(value) ? null : value;
}

/**
 * Detecta unidade a partir de string (símbolo ou alias)
 */
function detectUnit(unitStr: string): TemperatureUnit | null {
  const normalized = unitStr.toLowerCase().trim();

  // Checa aliases primeiro
  if (normalized in TEMPERATURE_ALIASES) {
    return TEMPERATURE_ALIASES[normalized] as TemperatureUnit;
  }

  // Checa símbolos e nomes
  for (const [variant, config] of Object.entries(TEMPERATURE_UNITS)) {
    if (
      config.symbol.toLowerCase() === normalized ||
      config.singular?.toLowerCase() === normalized ||
      config.plural?.toLowerCase() === normalized ||
      variant.toLowerCase() === normalized
    ) {
      return variant as TemperatureUnit;
    }
  }

  return null;
}
